/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, FormControl, InputLabel, MenuItem, Modal, Select, TextField, Typography } from '@mui/material';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { showToast } from 'modules/layout/layout.actions';
import {
  getEmailTemplateSummaries,
  postRenderAuthorizationEmailTemplate,
  postSendRenderAuthorizationEmailTemplate
} from 'modules/emailTemplates/store/emailTemplate.actions';

import { getAuthorizationComments, getAuthorizationSentTemplates } from 'modules/authorizations/store/actions/authorizations.actions';
import { POST_SEND_RENDER_AUTHORIZATION_EMAIL_TEMPLATE_FAILURE } from '../store/emailTemplateTypes';
import { TOAST_MESSAGE_SEVERITY_ERROR } from 'modules/layout/types';
import { isIdEqual } from 'modules/enumerations/enumerationHelpers';
import { EMAIL_TEMPLATE_STATUS_IDS } from 'modules/enumerations/enumerationIds';
import { EmailEditorTiny } from '../emailEditors/EmailEditorTiny';

const STEP_SELECT = 1;
const STEP_EDIT = 2;

const AuthServiceButtonEmailCard = props => {
  const {
    id,
    communication,
    emailTemplates,
    selectedTemplateProp = '',
    openModalDefault = false,
    onUnselectTemplate,
    selectedAuthServiceId,
    selectedSegmentServiceId
  } = props;

  const [open, setOpen] = useState(openModalDefault);
  const [step, setStep] = useState(STEP_SELECT);
  const [selectedTemplateId, setSelectedTemplateId] = useState(selectedTemplateProp || '');
  const [primaryEmail, setPrimaryEmail] = useState('');
  const [isRendering, setIsRendering] = useState(false);
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [currentTemplateName, setCurrentTemplateName] = useState('');

  const subjectInputRef = useRef(null);

  const templateOptions = useMemo(
    () => (Array.isArray(emailTemplates) ? emailTemplates.filter(s => isIdEqual(s.versionStatusId, EMAIL_TEMPLATE_STATUS_IDS.ACTIVE)) : []),
    [emailTemplates]
  );

  // const selectedTemplate = useMemo(() => templateOptions.find(t => t.templateId === selectedTemplateId) || null, [
  //   templateOptions,
  //   selectedTemplateId
  // ]);

  const resetState = () => {
    setStep(STEP_SELECT);
    setSelectedTemplateId('');
    setPrimaryEmail('');
    setIsRendering(false);
    setSubject('');
    setBodyHtml('');
    setCurrentTemplateName('');
  };

  const handleClose = () => {
    setOpen(false);
    resetState();

    if (onUnselectTemplate) {
      onUnselectTemplate();
    }
  };

  const renderTemplate = async templateId => {
    if (!templateId) return;

    try {
      setIsRendering(true);

      const renderRequest = {
        segmentId: selectedSegmentServiceId
      };

      const resp = await props.postRenderAuthorizationEmailTemplate(templateId, id, renderRequest);
      const data = resp?.response ?? resp;

      setSubject(data.renderedSubject || '');
      setBodyHtml(data.renderedHtmlBody || '');

      const template = templateOptions.find(t => t.templateId === templateId);
      setCurrentTemplateName(template?.templateName || '');

      const messages = data?.validationResult?.nullOrEmptyTokens || [];
      if (messages.length > 0) {
        let message = 'The following fields were missing or incomplete when rendering this letter:\n';
        messages.forEach(m => {
          message += `- ${m}\n`;
        });

        const proceed = window.confirm(message);
        if (!proceed) {
          return;
        }
      }

      setStep(STEP_EDIT);
    } catch (err) {
      console.error('Failed to render email template', err);
      props.showToast('Failed to render email template', 'error');
    } finally {
      setIsRendering(false);
    }
  };

  const handleOpen = async () => {
    setOpen(true);
    setPrimaryEmail(communication?.primaryEmail ?? communication?.webAccessEmailAddress ?? '');

    if (templateOptions.length === 0) {
      setSelectedTemplateId('');
      setStep(STEP_SELECT);
      return;
    }

    const firstTemplateId = selectedTemplateProp || templateOptions[0]?.templateId || '';
    setSelectedTemplateId(firstTemplateId);

    // if (templateOptions.length === 1) {
    //   await renderTemplate(firstTemplateId);
    // } else {
    //   setStep(STEP_SELECT);
    // }
  };

  useEffect(() => {
    if (openModalDefault) {
      handleOpen();
    }
  }, [openModalDefault]);

  const handleSendClick = async () => {
    if (!primaryEmail) {
      props.showToast('No email address available to send to.', 'error');
      return;
    }

    const request = {
      authorizationId: parseInt(id, 10),
      renderedSubject: subject || '',
      renderedHtmlBody: bodyHtml || '',
      templateId: selectedTemplateId ? Number(selectedTemplateId) : 0,
      emailAddresses: [primaryEmail],
      fromAddress: 'test-relo@trcgm.com',
      authServiceId: selectedAuthServiceId,
      segmentId: selectedSegmentServiceId
    };

    setIsRendering(true);

    try {
      const response = await props.postSendRenderAuthorizationEmailTemplate(selectedTemplateId, id, request);

      if (response.type === POST_SEND_RENDER_AUTHORIZATION_EMAIL_TEMPLATE_FAILURE) {
        props.showToast('Error sending template', TOAST_MESSAGE_SEVERITY_ERROR);
      } else {
        await props.getAuthorizationSentTemplates(id);
        await props.getAuthorizationComments(id);
        await props.getEmailTemplateSummaries();
        props.showToast('Email sent successfully.', 'success');
        handleClose();
      }
    } catch (err) {
      console.error('Failed to send rendered email', err);
      props.showToast('Failed to send email.', 'error');
    } finally {
      setIsRendering(false);
    }
  };

  return (
    <>
      {!openModalDefault && (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '5px' }}>
            <Button
              variant="contained"
              onClick={handleOpen}
              sx={{
                maxHeight: '40px',
                marginLeft: '12px',
                marginTop: '8px',
                backgroundColor: '#868686',
                '&:hover': { backgroundColor: '#474747' }
              }}
            >
              Send Email
            </Button>
          </Box>
        </Box>
      )}

      <Modal open={open} onClose={handleClose} aria-labelledby="email-modal">
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 1100,
            maxHeight: '100vh',
            overflowY: 'hidden',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 4,
            borderRadius: '8px',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {step === STEP_SELECT && (
            <>
              <Typography variant="h6" id="email-modal" gutterBottom>
                Select Email Recipient & Template
              </Typography>

              <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    label="Primary Contact Email"
                    value={primaryEmail}
                    onChange={e => setPrimaryEmail(e.target.value)}
                    margin="dense"
                  />
                </Box>

                <Box sx={{ flex: 1 }}>
                  <FormControl variant="standard" fullWidth sx={{ mt: 0.5 }}>
                    <InputLabel id="email-template-label">Select Template</InputLabel>
                    <Select
                      labelId="email-template-label"
                      value={selectedTemplateId}
                      onChange={e => setSelectedTemplateId(e.target.value)}
                      label="Select Template"
                    >
                      {templateOptions.map(t => (
                        <MenuItem key={t.templateId} value={t.templateId}>
                          {t.templateName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  marginTop: '10px'
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => renderTemplate(selectedTemplateId)}
                  disabled={!selectedTemplateId || isRendering}
                >
                  {isRendering ? 'Loading...' : 'Create Email'}
                </Button>
              </Box>
            </>
          )}

          {step === STEP_EDIT && (
            <>
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  pr: 1
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Review Email
                </Typography>

                <Box sx={{ mb: 1, p: 1.5, borderRadius: 1, bgcolor: 'grey.100' }}>
                  {currentTemplateName && (
                    <>
                      <Typography variant="subtitle2">
                        Template: <strong>{currentTemplateName}</strong>
                      </Typography>
                      <Typography variant="caption" display="block">
                        To: {primaryEmail}
                      </Typography>
                    </>
                  )}
                </Box>

                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Subject
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  inputRef={subjectInputRef}
                />

                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  Body
                </Typography>
                <Box
                  sx={{
                    minHeight: 300,
                    pb: 1,
                    '& .ql-container': {
                      minHeight: 250
                    }
                  }}
                >
                  <EmailEditorTiny initialHtml={bodyHtml} onHtmlChange={setBodyHtml} />
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginTop: '16px',
                  pt: 2,
                  borderTop: '1px solid #e0e0e0',
                  flexShrink: 0,
                  bgcolor: 'background.paper'
                }}
              >
                <Box>{templateOptions.length > 1 && <Button onClick={() => setStep(STEP_SELECT)}>Back</Button>}</Box>
                <Box>
                  <Button onClick={handleClose} sx={{ marginRight: '8px' }}>
                    Cancel
                  </Button>
                  <Button variant="contained" color="primary" onClick={handleSendClick} disabled={isRendering}>
                    {isRendering ? 'Sending...' : 'Send'}
                  </Button>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </>
  );
};

const mapStateToProps = state => ({});

export default compose(
  connect(mapStateToProps, {
    showToast,
    postRenderAuthorizationEmailTemplate,
    postSendRenderAuthorizationEmailTemplate,
    getAuthorizationSentTemplates,
    getAuthorizationComments,
    getEmailTemplateSummaries
  })
)(AuthServiceButtonEmailCard);
